import { useLayoutEffect, useRef, useState } from 'react';
import type { Product } from '@/api/products';
import MockupRenderer from '@/components/editor/MockupRenderer';
import { useDirectSaleMockupInput } from './useDirectSaleMockupInput';
import { cn } from '@/lib/utils';

/**
 * The product with the customer's design on it, outside the editor.
 *
 * A position's stored `preview` is only the design, exported from the print
 * area. Showing it raw loses everything that makes it a product: the garment
 * colour behind the cutout mask, the heathered pattern under the design, the
 * shading above it (below it for embroidery, and on the photo for mugs and
 * cushions), the overlay outside the mask. MockupRenderer owns those rules —
 * the same component the Seller flow's cards and publish summary go through
 * — so Direct Sale feeds it rather than re-deriving a stack of its own.
 *
 * No adapted placement and no crop: Direct Sale previews the design on the
 * very product it was made on.
 */
export function DirectSaleMockupPreview({
  product,
  positionSlug,
  designDataUrl,
  colorId: colorIdOverride,
  fit = 0.92,
  lazyLoad = false,
  className,
}: {
  product: Product;
  positionSlug: string;
  designDataUrl: string | null | undefined;
  /** Another colour than the selected one (the colour picker's thumbs). */
  colorId?: string;
  /** Share of the container the mockup may fill. */
  fit?: number;
  lazyLoad?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState({ width: 0, height: 0 });
  const { template, backgroundColor, isHeathered, isEmbroidery } =
    useDirectSaleMockupInput(product, positionSlug, colorIdOverride);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setMeasured({ width: rect.width, height: rect.height });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const mockupWidth = template?.mockupWidthPx || 500;
  const mockupHeight = template?.mockupHeightPx || 500;
  const scale =
    Math.min(measured.width / mockupWidth, measured.height / mockupHeight) *
    fit;

  return (
    <div
      ref={containerRef}
      className={cn('flex items-center justify-center', className)}
    >
      {template && scale > 0 && (
        <MockupRenderer
          productId={product.id}
          template={template}
          backgroundColor={backgroundColor}
          designDataUrl={designDataUrl ?? null}
          isHeathered={isHeathered}
          isEmbroidery={isEmbroidery}
          // The surface behind the preview belongs to the caller.
          frameBackground="transparent"
          width={Math.round(mockupWidth * scale)}
          height={Math.round(mockupHeight * scale)}
          lazyLoad={lazyLoad}
          priority={lazyLoad ? 'low' : 'high'}
        />
      )}
    </div>
  );
}
